import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function getInitials(nombre) {
  if (!nombre) return '?';
  const parts = nombre.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return nombre[0].toUpperCase();
}

function normalizeGenero(genero) {
  if (!genero) return null;
  const v = String(genero).trim().toUpperCase();
  if (v === 'M' || v.startsWith('MASC')) return 'M';
  if (v === 'F' || v.startsWith('FEM')) return 'F';
  return v;
}

function getGeneroMeta(inv) {
  const g = normalizeGenero(inv?.genero);
  if (g === 'Masculino') {
    return {
      label: 'Masculino',
      icon: 'male',
      iconClass: 'text-[#2D5BA6]',
      badgeBg: 'bg-[#2D5BA6]/10 dark:bg-[#2D5BA6]/15',
      badgeBorder: 'border-[#2D5BA6]/20 dark:border-[#2D5BA6]/25',
      badgeText: 'text-[#2D5BA6] dark:text-[#5B8BD9]',
      avatarGradient: 'from-[#2D5BA6] to-blue-400'
    };
  }
  if (g === 'Femenino') {
    return {
      label: 'Femenino',
      icon: 'female',
      iconClass: 'text-[#E11D48]',
      badgeBg: 'bg-[#E11D48]/10 dark:bg-[#E11D48]/15',
      badgeBorder: 'border-[#E11D48]/20 dark:border-[#E11D48]/25',
      badgeText: 'text-[#E11D48] dark:text-[#FB7185]',
      avatarGradient: 'from-[#E11D48] to-rose-400'
    };
  }
  return null;
}

function StepSelectInvestigador({ universidad, investigadores, loading, onSelect, onBack }) {
  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce para la búsqueda
  useEffect(() => {
    setIsSearching(true);
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 300); // 300ms de delay

    return () => clearTimeout(timer);
  }, [search]);

  // Filtrar investigadores
  const filtered = useMemo(() => {
    if (!debouncedSearch) return investigadores;
    
    const searchLower = debouncedSearch.toLowerCase();
    return investigadores.filter((inv) =>
      inv.nombre.toLowerCase().includes(searchLower) ||
      (inv.area_conocimiento || '').toLowerCase().includes(searchLower)
    );
  }, [investigadores, debouncedSearch]);

  // Determinar si mostrar spinner
  const showSpinner = loading || isSearching;

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-[#2D5BA6] dark:hover:text-[#5B8BD9] transition-colors mb-4"
        >
          <span className="material-icons-round text-base">arrow_back</span>
          Cambiar universidad
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
            <span className="material-icons-round text-xl text-[#2D5BA6]">school</span>
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide font-semibold">Universidad seleccionada</p>
            <p className="font-bold text-slate-900 dark:text-white">{universidad.name}</p>
          </div>
        </div>

        <h2 className="text-xl font-bold text-slate-900 dark:text-white mt-4">Seleccionar Investigador</h2>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
          {loading ? 'Cargando investigadores...' : `${filtered.length} investigadores encontrados`}
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl">search</span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre o área de conocimiento..."
          className="w-full pl-10 pr-12 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2D5BA6] transition-all"
        />
        {/* Spinner de búsqueda */}
        <AnimatePresence>
          {isSearching && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <div className="w-5 h-5 border-2 border-[#2D5BA6] border-t-transparent rounded-full animate-spin" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showSpinner ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-[#2D5BA6] border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-500 dark:text-slate-400">
              {loading ? 'Cargando investigadores...' : 'Buscando...'}
            </span>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <span className="material-icons-round text-5xl text-slate-300 dark:text-slate-600">person_search</span>
          <p className="text-slate-500 dark:text-slate-400 mt-3">
            {investigadores.length === 0
              ? 'No hay investigadores registrados para esta universidad'
              : 'No se encontraron resultados para tu búsqueda'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((inv, idx) => {
            const meta = getGeneroMeta(inv);
            return (
            <motion.button
              key={inv.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.03 }}
              onClick={() => onSelect(inv)}
              className="group flex flex-col items-center gap-3 p-5 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl hover:border-[#2D5BA6] dark:hover:border-[#5B8BD9] hover:shadow-lg transition-all text-center"
            >
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${meta?.avatarGradient || 'from-[#2D5BA6] to-blue-400'} flex items-center justify-center text-white font-bold text-xl shadow-md group-hover:shadow-lg transition-all`}>
                {getInitials(inv.nombre)}
              </div>
              <div className="min-w-0 w-full">
                <p className="font-semibold text-slate-900 dark:text-white text-sm leading-tight line-clamp-2">{inv.nombre}</p>
                {inv.area_conocimiento && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-1">{inv.area_conocimiento}</p>
                )}
                {inv.orcid && (
                  <p className="text-xs text-[#2D5BA6] dark:text-[#5B8BD9] mt-1 font-mono truncate">{inv.orcid}</p>
                )}
              </div>

              {meta ? (
                <span
                  className={`flex items-center gap-1 text-xs font-medium ${meta.badgeBg} ${meta.badgeBorder} ${meta.badgeText} border px-2 py-0.5 rounded-full`}
                  title={meta.label}
                >
                  <span className={`material-icons-round text-sm ${meta.iconClass}`}>{meta.icon}</span>
                  {meta.label}
                </span>
              ) : null}

              {inv.open_alex_id ? (
                <span className="flex items-center gap-1 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">
                  <span className="material-icons-round text-sm">check_circle</span>
                  OpenAlex
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full">
                  <span className="material-icons-round text-sm">warning</span>
                  Sin OpenAlex ID
                </span>
              )}
            </motion.button>
          );})}
        </div>
      )}
    </div>
  );
}

export default StepSelectInvestigador;
